import * as React from "react";
import { EntityViewDialog } from "./EntityViewDialog";

export type SnapshotModalEntry = { entityName: string; data: any };

/**
 * A function the caller provides so the hook can render the body of the
 * currently-open modal. The caller knows which dedicated `Snapshot<X>View`
 * component matches each entity type — this hook does not. Returning `null`
 * tells the modal there is nothing to show.
 */
export type RenderSnapshotEntityFn = (
  entityName: string,
  data: any,
) => React.ReactNode;

/**
 * Shared drilldown-modal state for every snapshot view emitted by the
 * generator. Owns:
 *   * a stack of `{entityName, data}` entries — clicking a chip / row pushes,
 *     closing the dialog pops;
 *   * the rendering of `EntityViewDialog` for the top entry.
 *
 * Each generated `Snapshot<X>View` calls this hook once with a switch that
 * routes the entity-name to its sibling Snapshot view component.
 */
export function useSnapshotModalStack(renderEntityView: RenderSnapshotEntityFn): {
  pushModal: (entityName: string, data: any) => void;
  modalElement: React.ReactNode;
} {
  const [stack, setStack] = React.useState<SnapshotModalEntry[]>([]);
  const pushModal = React.useCallback(
    (entityName: string, data: any) =>
      setStack((prev) => [...prev, { entityName, data }]),
    [],
  );
  const popModal = React.useCallback(
    () => setStack((prev) => prev.slice(0, -1)),
    [],
  );
  const top = stack[stack.length - 1];
  const modalElement = top ? (
    <EntityViewDialog open={true} onClose={popModal}>
      {renderEntityView(top.entityName, top.data)}
    </EntityViewDialog>
  ) : null;
  return { pushModal, modalElement };
}
